import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { AlertTriangle, Clock, DollarSign, Users, Calendar, Filter, RefreshCw, CheckCircle, Bell, ChevronDown, Heart, Thermometer, Brain, Activity } from 'lucide-react';
import api from '../api';

const AlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, high: 0, medium: 0, low: 0, byCategory: {} });
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, filterPriority]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      let url = '/alerts';
      const params = [];
      if (filterCategory !== 'all') params.push(`category=${filterCategory}`);
      if (filterPriority !== 'all') params.push(`priority=${filterPriority}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await api.get(url);
      if (response.data.success) {
        setAlerts(response.data.alerts);
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedDemoData = async () => {
    setSeeding(true);
    try {
      await api.post('/seed-demo-data');
      await fetchAlerts();
    } catch (error) {
      console.error('Error seeding demo data:', error);
    } finally {
      setSeeding(false);
    }
  };

  const getCategoryIcon = (category, type) => {
    // For clinical alerts, use more specific icons based on alert type
    if (category === 'clinical') {
      if (type === 'vital_sign') return <Heart className="w-5 h-5 text-red-500" />;
      if (type === 'critical_value') return <Brain className="w-5 h-5 text-purple-500" />;
      return <Activity className="w-5 h-5 text-green-500" />;
    }
    
    switch (category) {
      case 'staff':
        return <Users className="w-5 h-5" />;
      case 'billing':
        return <DollarSign className="w-5 h-5" />;
      case 'scheduling':
        return <Calendar className="w-5 h-5" />;
      default:
        return <AlertTriangle className="w-5 h-5" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500 text-white';
      case 'medium':
        return 'bg-yellow-500 text-white';
      case 'low':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'staff':
        return 'bg-purple-100 text-purple-800';
      case 'clinical':
        return 'bg-green-100 text-green-800';
      case 'billing':
        return 'bg-orange-100 text-orange-800';
      case 'scheduling':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="alerts-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Alerts & Notifications</h1>
            <p className="text-gray-600 mt-1">Monitor time-sensitive items across your agency</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchAlerts}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="refresh-alerts-button"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={seedDemoData}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2 bg-[#ea580c] text-white rounded-lg hover:bg-[#dc5612] transition-colors disabled:opacity-50"
              data-testid="seed-demo-data-button"
            >
              {seeding ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  Load Demo Data
                </>
              )}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Alerts</p>
                <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg">
                <Bell className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-red-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Urgent</p>
                <p className="text-2xl font-bold text-red-600">{summary.high}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-yellow-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Warning</p>
                <p className="text-2xl font-bold text-yellow-600">{summary.medium}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Info</p>
                <p className="text-2xl font-bold text-blue-600">{summary.low}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Category Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setFilterCategory(filterCategory === 'staff' ? 'all' : 'staff')}
            className={`p-4 rounded-xl border transition-all ${filterCategory === 'staff' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white hover:border-purple-300'}`}
            data-testid="filter-staff"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500">Staff</p>
                <p className="text-lg font-semibold text-gray-900">{summary.byCategory?.staff || 0}</p>
              </div>
            </div>
          </button>
          <button
            onClick={() => setFilterCategory(filterCategory === 'clinical' ? 'all' : 'clinical')}
            className={`p-4 rounded-xl border transition-all ${filterCategory === 'clinical' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-300'}`}
            data-testid="filter-clinical"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500">Clinical</p>
                <p className="text-lg font-semibold text-gray-900">{summary.byCategory?.clinical || 0}</p>
              </div>
            </div>
          </button>
          <button
            onClick={() => setFilterCategory(filterCategory === 'billing' ? 'all' : 'billing')}
            className={`p-4 rounded-xl border transition-all ${filterCategory === 'billing' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-orange-300'}`}
            data-testid="filter-billing"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500">Billing</p>
                <p className="text-lg font-semibold text-gray-900">{summary.byCategory?.billing || 0}</p>
              </div>
            </div>
          </button>
          <button
            onClick={() => setFilterCategory(filterCategory === 'scheduling' ? 'all' : 'scheduling')}
            className={`p-4 rounded-xl border transition-all ${filterCategory === 'scheduling' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'}`}
            data-testid="filter-scheduling"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500">Scheduling</p>
                <p className="text-lg font-semibold text-gray-900">{summary.byCategory?.scheduling || 0}</p>
              </div>
            </div>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded-xl border border-gray-200">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          <div className="relative">
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="appearance-none bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm focus:ring-2 focus:ring-[#ea580c] focus:border-[#ea580c]"
              data-testid="priority-filter"
            >
              <option value="all">All Priorities</option>
              <option value="high">Urgent Only</option>
              <option value="medium">Warning Only</option>
              <option value="low">Info Only</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
          {(filterCategory !== 'all' || filterPriority !== 'all') && (
            <button
              onClick={() => { setFilterCategory('all'); setFilterPriority('all'); }}
              className="text-sm text-[#ea580c] hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Alerts List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-[#ea580c] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Loading alerts...</p>
            </div>
          ) : alerts.length === 0 ? (
            <div className="p-12 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Alerts Found</h3>
              <p className="text-gray-500 mb-4">
                {filterCategory !== 'all' || filterPriority !== 'all' 
                  ? 'Try adjusting your filters or click "Load Demo Data" to see sample alerts.'
                  : 'Click "Load Demo Data" to generate sample alerts for demonstration.'}
              </p>
              <button
                onClick={seedDemoData}
                disabled={seeding}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#ea580c] text-white rounded-lg hover:bg-[#dc5612] transition-colors disabled:opacity-50"
              >
                {seeding ? 'Loading...' : 'Load Demo Data'}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {alerts.map((alert, index) => (
                <div
                  key={alert.id || index}
                  className={`p-4 hover:bg-gray-50 transition-colors ${alert.priority === 'high' ? 'bg-red-50/30' : ''}`}
                  data-testid={`alert-row-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${getPriorityColor(alert.priority)}`}>
                      {getCategoryIcon(alert.category, alert.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">{alert.title}</h3>
                          <p className="text-gray-600 mt-1">{alert.message}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityBadge(alert.priority)}`}>
                            {alert.priority.toUpperCase()}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${getCategoryColor(alert.category)}`}>
                            {alert.type === 'vital_sign' ? '❤️ Vital Sign' : 
                             alert.type === 'critical_value' ? '🧠 Critical Value' : 
                             alert.category}
                          </span>
                        </div>
                      </div>
                      {alert.dueDate && (
                        <p className="text-sm text-gray-500 mt-2">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Due: {new Date(alert.dueDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Alert Types Info */}
        <div className="bg-gradient-to-br from-[#ea580c]/5 to-[#f59e0b]/5 rounded-xl p-6 border border-[#ea580c]/20">
          <h3 className="font-semibold text-gray-900 mb-4">Alert Types Monitored</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-purple-700 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" /> Staff Alerts
              </h4>
              <ul className="text-gray-600 space-y-1">
                <li>• Workload exceeds threshold</li>
                <li>• License expiring/expired</li>
                <li>• CPR certification due</li>
                <li>• TB test expiring</li>
                <li>• Background check renewal</li>
                <li>• Physical exam due</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Clinical Alerts
              </h4>
              <ul className="text-gray-600 space-y-1">
                <li>• Doctor's orders due</li>
                <li>• Nurses notes due</li>
                <li>• HHA notes due</li>
                <li>• LVN notes due</li>
                <li>• Therapist notes due</li>
                <li>• OASIS assessment due</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-orange-700 mb-2 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Billing Alerts
              </h4>
              <ul className="text-gray-600 space-y-1">
                <li>• Claims submission due</li>
                <li>• Authorization expiring</li>
                <li>• Payment overdue</li>
                <li>• Billing deadlines</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-blue-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Scheduling Alerts
              </h4>
              <ul className="text-gray-600 space-y-1">
                <li>• Visits due today</li>
                <li>• Upcoming appointments</li>
                <li>• Missed visits</li>
                <li>• Schedule conflicts</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AlertsPage;
